import React from 'react';
import styled, { css } from 'styled-components/macro';
import SearchableMenu from 'components/base/Menu/SearchableMenu';

import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import colors from 'styles/themes/colors';
import Menu from '../../Menu/Menu';
import MediaIcon from './MediaIcon';

import { ListItemBoxBase } from 'components/base/Dropdown/shared/styles';
import DropdownButton from '../DropdownButton';
import DropdownWrapper from '../DropdownWrapper';

const ListWrapper = styled(Box)<any>`
  ${ListItemBoxBase}
  padding: ${({ theme }): string => `0.7rem ${theme.spacing.large}`};

  &:hover,
  &[aria-selected='true'] {
    background: ${colors.Sand};
  }

  ${({ isActive }): any => css`
    cursor: pointer;
    background: ${isActive ? colors.Sand : 'white'};
  `}
`;

const ListItem: React.FC<IListItemProps> = (props) => {
  const { item, isActive } = props;
  return (
    <ListWrapper
      isActive={isActive}
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      py={2}
      paddingRight={2}
      paddingLeft={3}
      minWidth="270px"
    >
      <Box
        alignSelf="auto"
        display="inline-block"
        minWidth="2rem"
        maxHeight="1rem"
        paddingLeft={1.5}
      >
        {item.name && <MediaIcon small aria-hidden mediaType={item.name} />}
      </Box>
      <Span marginLeft={1} alignSelf="auto">
        {item.label}
      </Span>
    </ListWrapper>
  );
};

const getButtonWithDefaultLabel = (
  clearFilter: () => void,
  defaultLabel?: string
): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return (
      <DropdownButton
        {...props}
        defaultLabel={defaultLabel}
        dropdownType="Media"
        clearFilter={clearFilter}
      />
    );
  };
  return LabeledButton;
};

const MediaDropdown: React.FC<IMediaDropdownProps> = (props) => {
  const {
    items,
    selectedItem,
    onChange,
    disabled,
    itemToString,
    defaultLabel,
    children,
    isTooltipEnabled,
    tooltipContent,
    clearFilter,
  } = props;

  if (disabled) {
    return (
      <div>
        <DropdownWrapper
          hasWideSearchableMenu
          isEnabled={!disabled}
          label="MEDIA TYPE"
        >
          <SearchableMenu
            items={items}
            selectedItem={selectedItem || null}
            onChange={onChange}
            disabled={disabled}
            itemToString={itemToString}
            Button={getButtonWithDefaultLabel(clearFilter, defaultLabel)}
            Item={ListItem}
            showListLabel
            disabledMessage={
              ' If you’d like to select a Media Type, please clear the Shipped Units from dropdowns.'
            }
            isTooltipEnabled={isTooltipEnabled}
            tooltipContent={tooltipContent}
          >
            {children}
          </SearchableMenu>
        </DropdownWrapper>
      </div>
    );
  }

  // selectedItem can be undefined which will trigger React into thinking this is a controlled component,
  // when it then switches to an actual value runtime errors will be thrown since React will figure
  // we're trying to switch a controlled component into an uncontrolled component. Null is a valid value,
  // therefore we add this seemingly weird check.
  return (
    <DropdownWrapper label="MEDIA TYPE">
      <Menu
        items={items}
        selectedItem={selectedItem || null}
        onChange={onChange}
        disabled={disabled}
        itemToString={itemToString}
        Button={getButtonWithDefaultLabel(clearFilter, defaultLabel)}
        Item={ListItem}
        isTooltipEnabled={isTooltipEnabled}
        tooltipContent={tooltipContent}
      >
        {children}
      </Menu>
    </DropdownWrapper>
  );
};

interface IMediaDropdownProps {
  items: any[];
  selectedItem: any;
  onChange: (item: any) => void;
  disabled?: boolean;
  itemToString?: (item: any) => string | null;
  defaultLabel?: string;
  children?: any;
  isTooltipEnabled?: boolean;
  tooltipContent?: string | JSX.Element;
  clearFilter: () => void;
}

interface IListItemProps {
  item: any;
  isActive?: boolean;
  isHovered?: boolean;
}

export default MediaDropdown;
